/**
 * Transfers — `/inventory/transfers`.
 *
 * Cursor list of transfer documents with their monotonic lifecycle
 * (`draft → in_transit → received | cancelled`). The row status decides which
 * transitions the detail screen can offer; nothing is inferred client-side.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";
import { Plus } from "lucide-react";

import { DataTable, badgeCell, dateTimeCell, textCell } from "@/components/data-table";

import { TRANSFER_STATUSES, type TransferStatus, type TransferSummary } from "../api";
import { useTransferList, useWarehouseOptions, type TransferFilters } from "../queries";
import { ALL } from "../components/pickers";

const TRANSFER_BADGE_VARIANTS = {
  draft: "outline",
  in_transit: "warning",
  received: "success",
  cancelled: "secondary",
} as const;

export interface TransfersPageProps {
  canCreate?: boolean;
  onOpenTransfer?: (transferId: string) => void;
  onNewTransfer?: () => void;
  pageLimit?: number;
}

export function TransfersPage({
  canCreate = false,
  onOpenTransfer,
  onNewTransfer,
  pageLimit,
}: TransfersPageProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string>(ALL);
  const [warehouseId, setWarehouseId] = useState<string>(ALL);

  const warehouses = useWarehouseOptions().data ?? [];
  const warehouseCode = (id: string): string =>
    warehouses.find((warehouse) => warehouse.id === id)?.code ?? id;

  const filters = useMemo<TransferFilters>(() => {
    const built: TransferFilters = {};
    if (status !== ALL) built.status = status as TransferStatus;
    if (warehouseId !== ALL) built.warehouse_id = warehouseId;
    return built;
  }, [status, warehouseId]);

  const query = useTransferList(filters, pageLimit);

  const columns = useMemo<ColumnDef<TransferSummary>[]>(
    () => [
      {
        accessorKey: "transfer_number",
        id: "transfer_number",
        header: t("inventory.transfers.columns.number", "Transfer"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono text-xs font-medium">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "source_warehouse_id",
        id: "source",
        header: t("inventory.transfers.columns.from", "From"),
        enableSorting: false,
        cell: (ctx) => textCell(warehouseCode(ctx.getValue<string>())),
      },
      {
        accessorKey: "destination_warehouse_id",
        id: "destination",
        header: t("inventory.transfers.columns.to", "To"),
        enableSorting: false,
        cell: (ctx) => textCell(warehouseCode(ctx.getValue<string>())),
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("inventory.common.status", "Status"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(
            value,
            TRANSFER_BADGE_VARIANTS,
            t(`inventory.transferStatus.${value}`, value),
          );
        },
      },
      {
        accessorKey: "requested_at",
        id: "requested_at",
        header: t("inventory.transfers.columns.requested", "Requested"),
        enableSorting: false,
        cell: (ctx) => dateTimeCell(ctx.getValue<string>()),
      },
      {
        accessorKey: "shipped_at",
        id: "shipped_at",
        header: t("inventory.transfers.columns.shipped", "Shipped"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string | undefined>();
          return value === undefined ? textCell(undefined) : dateTimeCell(value);
        },
      },
      {
        accessorKey: "received_at",
        id: "received_at",
        header: t("inventory.transfers.columns.received", "Received"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string | undefined>();
          return value === undefined ? textCell(undefined) : dateTimeCell(value);
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- warehouseCode derives from the loaded options
    [t, warehouses],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t("inventory.transfers.title", "Transfers")}</h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "inventory.transfers.subtitle",
              "Stock moving between warehouses, with both ledger legs tied to one document.",
            )}
          </p>
        </div>
        {canCreate && onNewTransfer !== undefined ? (
          <Button type="button" onClick={onNewTransfer}>
            <Plus className="size-4" aria-hidden="true" />
            {t("inventory.transfers.new", "New transfer")}
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="transfer-status">{t("inventory.common.status", "Status")}</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger id="transfer-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("inventory.common.all", "All")}</SelectItem>
              {TRANSFER_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`inventory.transferStatus.${value}`, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="transfer-warehouse">{t("inventory.common.warehouse", "Warehouse")}</Label>
          <Select value={warehouseId} onValueChange={setWarehouseId}>
            <SelectTrigger id="transfer-warehouse">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("inventory.common.all", "All")}</SelectItem>
              {warehouses.map((warehouse) => (
                <SelectItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.code} — {warehouse.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {t("inventory.transfers.warehouseHint", "Matches either side of the transfer.")}
          </p>
        </div>
      </div>

      <DataTable
        columns={columns}
        query={query}
        getRowId={(row) => row.id}
        onRowClick={(row) => onOpenTransfer?.(row.id)}
        storageKey="inventory.transfers.list.columns"
        emptyMessage={t("inventory.transfers.empty", "No transfers yet.")}
        errorMessage={t("inventory.transfers.loadError", "Transfers could not be loaded.")}
      />
    </div>
  );
}
